import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import { isEmpty } from 'lodash';
import usePipelineFeedback from '@hooks/usePipelineFeedback';
import useQueryDebugLogs from '@hooks/useQueryDebugLogs';
import { useScreen } from '@hooks/useScreen';
import { StatusCodes } from '@constants/enum/common';
import { PipelineOutputType, PipelineStatusCodes } from '@constants/pipelines';
import { APPLY_AND_SEARCH_BUTTON, SEARCH_BUTTON } from '@constants/search';
import { setAppliedMetaDataFilterValues } from '@redux/actions/metadataFiltersActions';
import {
  appliedMetaFilterValuesSelector,
  selectedMetaFilterValuesSelector,
} from '@redux/selectors/metadataFiltersSelectors';
import { IMessage, IPipeline, ISearchResult, MetadataFiltersValue } from '@redux/types/types';
import { ChatInput } from '@components/ChatInput/ChatInput';
import SearchCardLayout from '@components/search/layouts/searchCard/SearchCardLayout';
import { WidgetLayout } from '@components/search/layouts/widgetLayout/WidgetLayout';
import useActivatePlaygroundPipeline from '@pages/search/hooks/useActivatePlaygroundPipeline';
import RenderBody from './RenderBody';
import styles from './search.module.scss';
import PlaygroundHeader from '../header/PlaygroundHeader';
import SearchMetadataFilters from '../searchMetadataFilters/SearchMetadataFilters';

export type SearchProps = {
  query: string;
  filters: Record<string, MetadataFiltersValue>;
  params?: Record<string, string>;
  files?: string[];
};

interface Props {
  isWidget?: boolean;
  isExternal?: boolean;
  selectedPipeline?: IPipeline;
  selectedPipelineName?: string;
  handleSearch: (_: SearchProps) => void;
  searchResult: ISearchResult | null;
  searchStatus: StatusCodes;
  displayMetaDataFilters: boolean;
  displayResultsFileOptions: boolean;
  displayResultsMoreOptions: boolean;
  displayFeedbackOptions?: boolean;
  searchFiltersVisible?: boolean;
  displayHeader?: boolean;
  onSearchFiltersClose?: () => void;
  onToggleFiltersClick?: () => void;
  message?: IMessage;
}

type Query = { query: string; files: string[] };

const SearchComponent = (props: Props) => {
  const {
    isWidget = false,
    isExternal,
    selectedPipeline,
    selectedPipelineName,
    handleSearch,
    searchResult,
    searchStatus,
    displayMetaDataFilters,
    displayResultsFileOptions,
    displayResultsMoreOptions,
    displayFeedbackOptions = true,
    searchFiltersVisible = true,
    displayHeader = true,
    onSearchFiltersClose,
    onToggleFiltersClick,
    message,
  } = props;
  const dispatch = useDispatch();
  const { getFeedbackTags, resetFeedbackTags } = usePipelineFeedback();
  const { activatePipeline, activatingPipeline, resetActivatePipelineActionStatus, pipelineType } =
    useActivatePlaygroundPipeline({
      isExternal,
      selectedPipelineName,
      allwaysPollSelectedPipeline: true,
    });

  const { toggleDebugLogsModal } = useQueryDebugLogs();

  const { isMobileScreen, isTabletScreen } = useScreen();
  const isSmallerScreen = isMobileScreen || isTabletScreen;

  const [params, setParams] = React.useState({});
  const onParamsChange = React.useCallback((value: Record<string, unknown>) => {
    setParams((prev) => ({ ...prev, ...value }));
  }, []);

  const selectedMetaFilterValues = useSelector(selectedMetaFilterValuesSelector);
  const appliedMetaFilterValues = useSelector(appliedMetaFilterValuesSelector);

  const [userQuery, setUserQuery] = useState<Query>({
    query: '',
    files: [],
  });

  const { answers = [], documents = [] } = searchResult || {};
  const isEmptyResults = !answers.length && !documents.length;
  const isSearching = searchStatus === StatusCodes.IN_PROGRESS;

  const hasFilters =
    isEmptyResults &&
    !isSearching &&
    isEmpty(appliedMetaFilterValues) &&
    !isEmpty(selectedMetaFilterValues);

  const onSubmit = useCallback(
    async (query: string, files: string[]) => {
      if (!query) return;
      setUserQuery({ query, files });

      if (selectedPipeline?.status === PipelineStatusCodes.IDLE) {
        activatePipeline(selectedPipeline.name);
      }
    },
    [activatePipeline, selectedPipeline?.name, selectedPipeline?.status],
  );

  useEffect(() => {
    const canQueryPipeline =
      selectedPipeline?.status === PipelineStatusCodes.DEPLOYED &&
      (userQuery.query || userQuery.files.length);

    if (canQueryPipeline) {
      handleSearch({ filters: selectedMetaFilterValues, params, ...userQuery });
      dispatch(setAppliedMetaDataFilterValues(selectedMetaFilterValues));
    }
  }, [
    dispatch,
    handleSearch,
    params,
    selectedMetaFilterValues,
    selectedPipeline?.status,
    userQuery,
  ]);

  useEffect(() => {
    return () => {
      resetActivatePipelineActionStatus();
    };
  }, [resetActivatePipelineActionStatus]);

  useEffect(() => {
    if (selectedPipelineName) {
      setUserQuery({ query: '', files: [] });
    }

    resetFeedbackTags();
  }, [resetFeedbackTags, selectedPipelineName]);

  useEffect(() => {
    if (!selectedPipeline?.pipeline_id) return;

    getFeedbackTags({ pipelineId: selectedPipeline.pipeline_id, isExternal });
  }, [getFeedbackTags, isExternal, selectedPipeline?.pipeline_id]);

  const onClickApplyFilters = () => {
    if (isEmptyResults) return;

    handleSearch({ filters: selectedMetaFilterValues, params, ...userQuery });
  };

  const openDebugLogsModal = () => {
    toggleDebugLogsModal({
      isVisible: true,
    });
  };

  const renderHeader = () => {
    if (!displayHeader) return null;
    return (
      <PlaygroundHeader
        selectedPipeline={selectedPipeline}
        isLoading={isSearching}
        onViewDebugLogs={openDebugLogsModal}
        onParamsChange={onParamsChange}
        params={params}
      />
    );
  };

  const renderSearchBar = () => (
    <>
      {isSmallerScreen && !isWidget && (
        <div className={styles.searchBar_filtersButtonWrapper}>
          <Badge dot={!isEmpty(selectedMetaFilterValues) || !isEmpty(appliedMetaFilterValues)}>
            <Button size="large" icon={<FilterOutlined />} onClick={onToggleFiltersClick} />
          </Badge>
        </div>
      )}

      <ChatInput
        activatingPipeline={activatingPipeline}
        isChatting={isSearching}
        onSubmit={onSubmit}
        ctoText={hasFilters ? APPLY_AND_SEARCH_BUTTON : SEARCH_BUTTON}
        type={pipelineType as Exclude<PipelineOutputType, PipelineOutputType.CHAT>}
        allowUpload={pipelineType === PipelineOutputType.GENERATIVE}
      />
    </>
  );

  return (
    <>
      {isWidget ? (
        <WidgetLayout
          header={renderHeader()}
          searchBar={renderSearchBar()}
          body={
            <RenderBody
              isExternal={isExternal}
              searchResult={searchResult}
              searchStatus={searchStatus}
              selectedPipelineName={selectedPipelineName}
              selectedPipeline={selectedPipeline}
              displayResultsFileOptions={displayResultsFileOptions}
              displayResultsMoreOptions={displayResultsMoreOptions}
              displayFeedbackOptions={displayFeedbackOptions}
              answers={answers}
              documents={documents}
              query={userQuery.query}
              message={message}
              activatePipeline={activatePipeline}
            />
          }
        />
      ) : (
        <SearchCardLayout
          header={renderHeader()}
          sideMenu={
            searchFiltersVisible && (
              <SearchMetadataFilters
                pipelineName={selectedPipelineName!}
                displayMetaDataFilters={displayMetaDataFilters}
                isExternal={isExternal}
                searchStatus={searchStatus}
                onClickApplyFilters={onClickApplyFilters}
                onSearchFiltersClose={onSearchFiltersClose}
              />
            )
          }
          searchBar={renderSearchBar()}
          body={
            <RenderBody
              isExternal={isExternal}
              searchResult={searchResult}
              searchStatus={searchStatus}
              selectedPipelineName={selectedPipelineName}
              selectedPipeline={selectedPipeline}
              displayResultsFileOptions={displayResultsFileOptions}
              displayResultsMoreOptions={displayResultsMoreOptions}
              displayFeedbackOptions={displayFeedbackOptions}
              answers={answers}
              documents={documents}
              query={userQuery.query}
              message={message}
              activatePipeline={activatePipeline}
            />
          }
        />
      )}
    </>
  );
};

export default memo(SearchComponent);
